/**
 * Custom Hook for Renewable Energy Data Management
 *
 * This hook handles fetching, processing, and state management for
 * renewable energy data including solar, onshore wind, and offshore wind.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { renewablePotentialAPI } from '../services/api';
import { calculatePercentileThresholds } from '../utils/renewableUtils';
import toast from 'react-hot-toast';

export const useRenewableData = (countryIso) => {
  const [solarData, setSolarData] = useState(null);
  const [onshoreWindData, setOnshoreWindData] = useState(null);
  const [offshoreWindData, setOffshoreWindData] = useState(null);
  const [error, setError] = useState(null);
  const [loadingStates, setLoadingStates] = useState({
    onshore: false,
    offshore: false,
  });
  const [windErrors, setWindErrors] = useState({
    onshore: null,
    offshore: null,
  });

  const [solarThresholds, setSolarThresholds] = useState(null);
  const [onshoreWindThresholds, setOnshoreWindThresholds] = useState(null);
  const [offshoreWindThresholds, setOffshoreWindThresholds] = useState(null);

  const [solarDataLoading, setSolarDataLoading] = useState(true);
  const [solarThresholdsLoading, setSolarThresholdsLoading] = useState(false);
  const [windDataLoading, setWindDataLoading] = useState(true);
  const [windThresholdsLoading, setWindThresholdsLoading] = useState(false);

  const pendingSolarIsoRef = useRef(null);
  const pendingOnshoreIsoRef = useRef(null);
  const pendingOffshoreIsoRef = useRef(null);

  const getZoneColorMetric = (zone) => {
    const clustered = zone?.avg_re_cf;
    if (typeof clustered === 'number' && Number.isFinite(clustered)) return clustered;
    const raw = zone?.['Capacity Factor'];
    if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
    return 0;
  };

  const loadSolarData = useCallback(async () => {
    const iso = (countryIso || '').toUpperCase();
    if (!iso) return;

    try {
      pendingSolarIsoRef.current = iso;
      setSolarDataLoading(true);
      setSolarData(null);
      setError(null);
      setSolarThresholds(null);

      let response = null;
      try {
        response = await renewablePotentialAPI.getSolarClusters(iso);
      } catch (e) {
        response = null;
      }

      if (!response?.success || response?.data?.has_clustering === false) {
        response = await renewablePotentialAPI.getSolarZones(iso);
      }

      if (pendingSolarIsoRef.current !== iso) {
        return;
      }

      if (response.success) {
        setSolarData(response.data);
      } else {
        console.error('Solar data loading failed:', response.error);
        setError(response.error || 'Failed to load solar renewable zones data');
      }
    } catch (err) {
      if (pendingSolarIsoRef.current !== iso) {
        return;
      }
      console.error('Error loading solar data:', err);
      setError('Failed to load solar renewable zones data');
      toast.error('Failed to load solar renewable zones data');
    } finally {
      if (pendingSolarIsoRef.current === iso) {
        setSolarDataLoading(false);
      }
    }
  }, [countryIso]);

  const loadOnshoreWindData = useCallback(async () => {
    const iso = (countryIso || '').toUpperCase();
    if (!iso) return;

    try {
      pendingOnshoreIsoRef.current = iso;
      setLoadingStates((prev) => ({ ...prev, onshore: true }));
      setOnshoreWindData(null);
      setOnshoreWindThresholds(null);
      setWindErrors((prev) => ({ ...prev, onshore: null }));

      let response = null;
      try {
        response = await renewablePotentialAPI.getWindClusters(iso, 'onshore');
      } catch (e) {
        response = null;
      }

      if (!response?.success || response?.data?.has_clustering === false) {
        response = await renewablePotentialAPI.getWindZones(iso, 'onshore');
      }

      if (pendingOnshoreIsoRef.current !== iso) {
        return;
      }

      if (response.success) {
        setOnshoreWindData(response.data);
      } else {
        console.warn('Failed to load onshore wind data:', response.error);
        setWindErrors((prev) => ({ ...prev, onshore: response.error }));
        setOnshoreWindData(null);
      }
    } catch (err) {
      if (pendingOnshoreIsoRef.current !== iso) {
        return;
      }
      console.warn('Error loading onshore wind data:', err);
      setWindErrors((prev) => ({ ...prev, onshore: 'Failed to load onshore wind data' }));
      setOnshoreWindData(null);
    } finally {
      if (pendingOnshoreIsoRef.current === iso) {
        setLoadingStates((prev) => ({ ...prev, onshore: false }));
      }
    }
  }, [countryIso]);

  const loadOffshoreWindData = useCallback(async () => {
    const iso = (countryIso || '').toUpperCase();
    if (!iso) return;

    try {
      pendingOffshoreIsoRef.current = iso;
      setLoadingStates((prev) => ({ ...prev, offshore: true }));
      setOffshoreWindData(null);
      setOffshoreWindThresholds(null);
      setWindErrors((prev) => ({ ...prev, offshore: null }));

      let response = null;
      try {
        response = await renewablePotentialAPI.getWindClusters(iso, 'offshore');
      } catch (e) {
        response = null;
      }

      if (!response?.success || response?.data?.has_clustering === false) {
        response = await renewablePotentialAPI.getWindZones(iso, 'offshore');
      }

      if (pendingOffshoreIsoRef.current !== iso) {
        return;
      }

      if (response.success) {
        setOffshoreWindData(response.data);
      } else {
        console.warn('Failed to load offshore wind data:', response.error);
        setWindErrors((prev) => ({ ...prev, offshore: response.error }));
        setOffshoreWindData(null);
      }
    } catch (err) {
      if (pendingOffshoreIsoRef.current !== iso) {
        return;
      }
      console.warn('Error loading offshore wind data:', err);
      setWindErrors((prev) => ({ ...prev, offshore: 'Failed to load offshore wind data' }));
      setOffshoreWindData(null);
    } finally {
      if (pendingOffshoreIsoRef.current === iso) {
        setLoadingStates((prev) => ({ ...prev, offshore: false }));
      }
    }
  }, [countryIso]);

  useEffect(() => {
    if (!loadingStates.onshore && !loadingStates.offshore) {
      setWindDataLoading(false);
    } else {
      setWindDataLoading(true);
    }
  }, [loadingStates.onshore, loadingStates.offshore]);

  useEffect(() => {
    loadSolarData();
  }, [loadSolarData]);

  useEffect(() => {
    loadOnshoreWindData();
    loadOffshoreWindData();
  }, [loadOnshoreWindData, loadOffshoreWindData]);

  useEffect(() => {
    if (solarData?.grid_data?.length > 0) {
      setSolarThresholdsLoading(true);
      try {
        const stats = solarData?.statistics;
        if (
          stats?.has_clustering &&
          Number.isFinite(stats?.vmin) &&
          Number.isFinite(stats?.vmax) &&
          stats.vmin !== stats.vmax
        ) {
          setSolarThresholds({
            mode: 'continuous',
            vmin: stats.vmin,
            vmax: stats.vmax,
            label: 'Solar Capacity Factor',
          });
        } else {
          const capacityFactors = solarData.grid_data.map(getZoneColorMetric);
          const thresholds = calculatePercentileThresholds(capacityFactors, 'solar');
          setSolarThresholds(thresholds);
        }
      } catch (err) {
        console.error('Error calculating solar thresholds:', err);
        setError('Failed to calculate solar thresholds');
      } finally {
        setSolarThresholdsLoading(false);
      }
    } else {
      setSolarThresholds(null);
      setSolarThresholdsLoading(false);
    }
  }, [solarData]);

  useEffect(() => {
    if (onshoreWindData?.grid_data?.length > 0) {
      try {
        const stats = onshoreWindData?.statistics;
        if (
          stats?.has_clustering &&
          Number.isFinite(stats?.vmin) &&
          Number.isFinite(stats?.vmax) &&
          stats.vmin !== stats.vmax
        ) {
          setOnshoreWindThresholds({
            mode: 'continuous',
            vmin: stats.vmin,
            vmax: stats.vmax,
            label: 'Wind Capacity Factor',
          });
        } else {
          const capacityFactors = onshoreWindData.grid_data.map(getZoneColorMetric);
          const thresholds = calculatePercentileThresholds(capacityFactors, 'onshore_wind');
          setOnshoreWindThresholds(thresholds);
        }
      } catch (err) {
        console.error('Error calculating onshore wind thresholds:', err);
        setWindErrors((prev) => ({ ...prev, onshore: 'Failed to calculate onshore wind thresholds' }));
        setOnshoreWindThresholds(null);
      }
    } else {
      setOnshoreWindThresholds(null);
    }
  }, [onshoreWindData]);

  useEffect(() => {
    if (offshoreWindData?.grid_data?.length > 0) {
      try {
        const stats = offshoreWindData?.statistics;
        if (
          stats?.has_clustering &&
          Number.isFinite(stats?.vmin) &&
          Number.isFinite(stats?.vmax) &&
          stats.vmin !== stats.vmax
        ) {
          setOffshoreWindThresholds({
            mode: 'continuous',
            vmin: stats.vmin,
            vmax: stats.vmax,
            label: 'Offshore Wind Capacity Factor',
          });
        } else {
          const capacityFactors = offshoreWindData.grid_data.map(getZoneColorMetric);
          const thresholds = calculatePercentileThresholds(capacityFactors, 'offshore_wind');
          setOffshoreWindThresholds(thresholds);
        }
      } catch (err) {
        console.error('Error calculating offshore wind thresholds:', err);
        setWindErrors((prev) => ({ ...prev, offshore: 'Failed to calculate offshore wind thresholds' }));
        setOffshoreWindThresholds(null);
      }
    } else {
      setOffshoreWindThresholds(null);
    }
  }, [offshoreWindData]);

  useEffect(() => {
    const onshoreReady = onshoreWindData
      ? onshoreWindData.grid_data?.length > 0
        ? onshoreWindThresholds !== null
        : true
      : true;
    const offshoreReady = offshoreWindData
      ? offshoreWindData.grid_data?.length > 0
        ? offshoreWindThresholds !== null
        : true
      : true;

    if (onshoreReady && offshoreReady) {
      setWindThresholdsLoading(false);
    } else {
      setWindThresholdsLoading(true);
    }
  }, [onshoreWindData, offshoreWindData, onshoreWindThresholds, offshoreWindThresholds]);

  const isSolarLoading = solarDataLoading || solarThresholdsLoading;
  const isWindLoading = windDataLoading || windThresholdsLoading;

  return {
    solarData,
    onshoreWindData,
    offshoreWindData,
    loadingStates,
    isSolarLoading,
    isWindLoading,
    error,
    windErrors,
    solarThresholds,
    onshoreWindThresholds,
    offshoreWindThresholds,
    loadSolarData,
    loadOnshoreWindData,
    loadOffshoreWindData,
  };
};

export default useRenewableData;
